import { useState } from 'react';
import { api, ago, useApi, type ApiFailure } from '../lib/api.ts';
import { ErrorState, Loading, Sheet, Status, orgPath, useOrg, useToast } from '../lib/ui.tsx';

export function Integrations() {
  const org = useOrg();
  const toast = useToast();
  const info = useApi<{ providers: { id: string; name: string; status: string; detail: string }[]; apiBase: string }>(orgPath(org, '/integrations'), [org.id]);
  const keys = useApi<{ apiKeys: { id: string; name: string; prefix: string; scopes: string[]; lastUsedAt: string | null; expiresAt: string; revokedAt: string | null }[]; availableScopes: string[] }>(orgPath(org, '/api-keys'), [org.id]);
  const hooks = useApi<{ webhooks: { id: string; url: string; events: string[]; recentDeliveries: { status: string; event_type: string }[] | null }[]; events: string[] }>(orgPath(org, '/webhooks'), [org.id]);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const [csv, setCsv] = useState<{ text: string; result: { valid: number; errors: { row: number; message: string }[]; header?: string[] } | null }>({ text: '', result: null });
  async function newKey() {
    try {
      const r = await api<{ key: string }>('POST', orgPath(org, '/api-keys'), { name: `Key ${new Date().toLocaleDateString()}`, scopes: ['screens.read', 'reports.read'] });
      setSecret({ title: 'API key (read screens and reports)', value: r.key });
      void keys.reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  async function newHook() {
    const url = prompt('HTTPS endpoint URL');
    if (!url) return;
    try {
      const r = await api<{ secret: string }>('POST', orgPath(org, '/webhooks'), { url, events: hooks.data?.events ?? [] });
      setSecret({ title: 'Webhook signing secret', value: r.secret });
      void hooks.reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  async function importCsv(dryRun: boolean) {
    const header = csv.text.split(/\r?\n/)[0]?.split(',').map((h) => h.trim()) ?? [];
    const mapping = { sku: header.find((h) => /sku/i.test(h)) ?? header[0] ?? '', name: header.find((h) => /name|product|title/i.test(h)) ?? header[1] ?? '', price: header.find((h) => /price/i.test(h)), category: header.find((h) => /categor/i.test(h)) };
    try {
      const r = await api<{ valid?: number; imported?: number; errors: { row: number; message: string }[]; header?: string[] }>('POST', orgPath(org, '/products/import'), { csv: csv.text, mapping, dryRun });
      setCsv({ ...csv, result: { valid: r.valid ?? r.imported ?? 0, errors: r.errors, header: r.header } });
      if (!dryRun) toast(`Imported ${r.imported} products`);
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <>
      <div className="page-head"><div><h1>Integrations</h1><p>REST API (OpenAPI at <code>{info.data?.apiBase}/openapi.json</code>), signed webhooks and data imports.</p></div></div>
      {info.error && <ErrorState error={info.error} />}
      {!info.data ? <Loading /> : (
        <div className="grid grid-cards" style={{ marginBottom: 16 }}>
          {info.data.providers.map((p) => (
            <div key={p.id} className="card card-pad stack"><div className="row" style={{ justifyContent: 'space-between' }}><h3>{p.name}</h3><Status value={p.status === 'available' ? 'ok' : p.status === 'unavailable' ? undefined : 'info'} label={p.status === 'unavailable' ? 'Not connected' : p.status} /></div><span className="subtle">{p.detail}</span></div>
          ))}
        </div>
      )}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><h2>Product CSV import</h2></div>
        <div className="card-pad stack">
          <textarea className="input mono" rows={6} aria-label="CSV content" placeholder={'SKU,Name,Price,Category\nA-1,Oak table,129.00,Furniture'} value={csv.text} onChange={(e) => setCsv({ text: e.target.value, result: null })} />
          <div className="row"><button className="btn" disabled={!csv.text} onClick={() => void importCsv(true)}>Preview</button><button className="btn btn-primary" disabled={!csv.result || !csv.result.valid} onClick={() => void importCsv(false)}>Import {csv.result?.valid ?? ''} rows</button></div>
          {csv.result && <div className="subtle">{csv.result.valid} valid · {csv.result.errors.length} rejected{csv.result.errors.slice(0, 5).map((e) => <div key={e.row}>Row {e.row}: {e.message}</div>)}</div>}
        </div>
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><h2>API keys</h2><button className="btn btn-sm" onClick={() => void newKey()}>Create read-only key</button></div>
        <table className="table"><tbody>{keys.data?.apiKeys.map((k) => <tr key={k.id}><td>{k.name}<div className="subtle mono">{k.prefix}…</div></td><td className="subtle">{k.scopes.join(', ')}</td><td className="subtle">used {ago(k.lastUsedAt)}</td><td>{k.revokedAt ? <Status value="revoked" /> : <button className="btn btn-sm btn-danger" onClick={() => void api('DELETE', orgPath(org, `/api-keys/${k.id}`)).then(() => keys.reload())}>Revoke</button>}</td></tr>)}</tbody></table>
      </div>
      <div className="card">
        <div className="card-head"><h2>Webhooks</h2><button className="btn btn-sm" onClick={() => void newHook()}>Add endpoint</button></div>
        <table className="table"><tbody>{hooks.data?.webhooks.map((w) => <tr key={w.id}><td className="mono">{w.url}</td><td className="subtle">{w.events.join(', ')}</td><td>{(w.recentDeliveries ?? []).slice(0, 3).map((d, i) => <Status key={i} value={d.status} label={`${d.event_type} ${d.status}`} />)}</td></tr>)}</tbody></table>
      </div>
      {secret && (
        <Sheet title={secret.title} subtitle="Copy it now — it is shown only once." onClose={() => setSecret(null)} footer={<button className="btn btn-primary" onClick={() => void navigator.clipboard.writeText(secret.value).then(() => toast('Copied'))}>Copy</button>}>
          <code className="mono" style={{ wordBreak: 'break-all' }}>{secret.value}</code>
        </Sheet>
      )}
    </>
  );
}
